import React, { useEffect } from 'react';
import { QueryObserverRefetchErrorResult, QueryObserverSuccessResult } from 'react-query';
import { IFVContainer } from '../interfaces/FVContainer.interface';
import { useHistory, useParams } from 'react-router-dom';
import { IFVPackage } from '../interfaces/FVPackage.interface';
import Styled from '../styles/public/FerramentaVideoPublic.styled';
import Colors from 'enums/Colors';
import { MdArrowBackIos } from 'react-icons/md';
import { bucketS3, caminhoAnexosFerramentaVideo } from 'constants/EnvironmentData';
import StyledUnit from '../styles/FVPackageUnit.styled';
import { formatTitleWithIndexSizeFontAll, formatTitleWithPrefixIndex } from '../helpers/FormatTitleStyleHelper';
import { YellowButton } from 'components/UI/Buttons';
import MenuTypePackages from '../components/MenuTypePackages';
import { IsCustomMaxWidth } from 'core/util/WindowSize';

interface FVPacoteProps {
    // ...other props (history, location, match, etc.)...
    listFerramenta: QueryObserverRefetchErrorResult<IFVContainer, unknown> | QueryObserverSuccessResult<IFVContainer, unknown>;
    handleRedirect?: () => void;
    setMenuActive?: (menu: string) => void;
}

const FVPacote = ({ listFerramenta, handleRedirect, setMenuActive }: FVPacoteProps) => {
    const history = useHistory();
    const { urlPacote, urlFerramenta } = useParams<{ urlPacote: string; urlFerramenta: string }>();
    const [fetchedData, setFetchedData] = React.useState<IFVPackage | null>(null);
    const ref = React.useRef<HTMLDivElement>(null);

    const IS500pixel = IsCustomMaxWidth(500);

    const [hoveredIndexCard, setHoveredIndexCard] = React.useState<number | null>(null);

    const handleRefDivClick = () => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const goToBack = () => {
        history.replace(`/videos/${urlFerramenta}/home`);
    };

    const goToContents = (unidadeUrl: string) => {
        history.push(`/videos/${urlFerramenta}/${urlPacote}/${unidadeUrl}/conteudos`);
    };

    const getBucketFerramentaVideo = () => {
        return bucketS3() + caminhoAnexosFerramentaVideo();
    };

    useEffect(() => {
        if (listFerramenta.data && listFerramenta.data.pacotes) {
            const pacote = listFerramenta?.data?.pacotes.find(p => p.url === urlPacote);

            setFetchedData(pacote || null);
        }
    }, [listFerramenta.data]);

    return (
        <Styled.Global.Content style={{ paddingTop: '35px' }}>
            <MenuTypePackages handleUpdateMenu={setMenuActive} hidden={listFerramenta?.data?.notAccessibility ?? false} />
            <Styled.Package.Content>
                <Styled.Global.ButtonBack onClick={goToBack} aria-label="Voltar para a página inicial da ferramenta de vídeos">
                    <span style={{ color: Colors.Yellow }}>
                        <MdArrowBackIos /> Selecionar
                    </span>{' '}
                    outros {fetchedData?.nomenclature?.toString().toLowerCase()}
                </Styled.Global.ButtonBack>

                <Styled.Package.Page.Row>
                    <Styled.Package.Page.Column>
                        <Styled.Package.Page.SubTitle>
                            {fetchedData?.nomenclature} {fetchedData?.ordem}
                        </Styled.Package.Page.SubTitle>
                        <Styled.Package.Page.Title>{fetchedData?.title}</Styled.Package.Page.Title>
                        <Styled.Package.Page.Description dangerouslySetInnerHTML={{ __html: fetchedData?.description ?? '' }} />
                        <YellowButton
                            style={{
                                height: '52px',
                                minHeight: '52px',
                                maxWidth: 'fit-content',
                                textTransform: 'uppercase',
                                fontSize: '20px',
                                borderRadius: '8px',
                                marginTop: '32px',
                                order: 0,
                            }}
                            aria-label="Scroll para escolher uma unidade"
                            onClick={handleRefDivClick}
                        >
                            Escolher um(a){' '}
                            {fetchedData?.packageUnits && fetchedData?.packageUnits.length > 0
                                ? fetchedData?.packageUnits[0].nomenclature?.toString().toUpperCase()
                                : ''}
                        </YellowButton>
                    </Styled.Package.Page.Column>

                    <Styled.Package.Page.Card.Container>
                        <Styled.Package.Page.Card.Content className="contentImg" img={getBucketFerramentaVideo() + fetchedData?.capaFilepath} />
                        <Styled.Package.Page.Card.Content className="contentInfo" backcolor={Colors.Yellow}>
                            <h2>{fetchedData?.nomenclature}</h2>
                            <h1>{fetchedData?.ordem}</h1>
                        </Styled.Package.Page.Card.Content>
                    </Styled.Package.Page.Card.Container>
                </Styled.Package.Page.Row>
                <Styled.Global.Title>
                    <span style={{ color: Colors.Yellow }}>SELECIONE</span> UM(A){' '}
                    {fetchedData?.packageUnits && fetchedData?.packageUnits.length > 0
                        ? fetchedData?.packageUnits[0].nomenclature?.toString().toUpperCase()
                        : ''}
                </Styled.Global.Title>
                <Styled.Package.Page.ContentGrid ref={ref}>
                    {fetchedData && fetchedData.packageUnits && fetchedData.packageUnits.length === 0 && (
                        <p style={{ color: Colors.White, fontSize: 20, lineHeight: '32px' }}>
                            Este livro ainda não possui unidades cadastradas. Por favor, aguarde até que tenha unidades disponíveis.
                        </p>
                    )}

                    {fetchedData?.packageUnits
                        ?.sort((a, b) => a.ordem - b.ordem)
                        .map((unidade, index) => {
                            return (
                                <React.Fragment key={unidade.id}>
                                    <div>
                                        <StyledUnit.CardUnit.Container
                                            onMouseEnter={() => setHoveredIndexCard(index)}
                                            onMouseLeave={() => setHoveredIndexCard(null)}
                                            onClick={() => goToContents(unidade.url)}
                                        >
                                            <StyledUnit.CardUnit.Content backcolor={hoveredIndexCard === index ? Colors.Yellow : Colors.White}>
                                                <h2>{unidade.nomenclature}</h2>
                                                <h1>{index + 1}</h1>
                                            </StyledUnit.CardUnit.Content>
                                            <StyledUnit.CardUnit.Content img={getBucketFerramentaVideo() + fetchedData?.capaFilepath} />
                                        </StyledUnit.CardUnit.Container>

                                        {!unidade?.description && (
                                            <StyledUnit.CardUnit.Description hovered={hoveredIndexCard === index} key={unidade.id}>
                                                <h1
                                                    dangerouslySetInnerHTML={{
                                                        __html: formatTitleWithIndexSizeFontAll(unidade.title, index),
                                                    }}
                                                />
                                            </StyledUnit.CardUnit.Description>
                                        )}

                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: IS500pixel ? 'start' : 'end',
                                                justifyContent: IS500pixel ? 'start' : 'end',
                                                paddingRight: '22px',
                                            }}
                                        >
                                            <YellowButton
                                                onMouseEnter={() => setHoveredIndexCard(index)}
                                                onMouseLeave={() => setHoveredIndexCard(null)}
                                                onFocusCapture={() => setHoveredIndexCard(index)}
                                                onClick={() => goToContents(unidade.url)}
                                                style={{
                                                    height: '52px',
                                                    minHeight: '52px',
                                                    maxWidth: 'fit-content',
                                                    textTransform: 'uppercase',
                                                    fontSize: '20px',
                                                    borderRadius: '8px',
                                                    order: 0,
                                                }}
                                                aria-label={`Acessar a unidade ${unidade.title}`}
                                            >
                                                Acessar {unidade.nomenclature}
                                            </YellowButton>
                                        </div>
                                    </div>
                                    {unidade?.description?.length > 0 && (
                                        <StyledUnit.CardUnit.Information key={unidade.id}>
                                            <h2 dangerouslySetInnerHTML={{ __html: formatTitleWithPrefixIndex(unidade.title, index) }} />
                                            <h1>{unidade.title}</h1>
                                            <p dangerouslySetInnerHTML={{ __html: unidade.description }} />
                                        </StyledUnit.CardUnit.Information>
                                    )}
                                </React.Fragment>
                            );
                        })}
                </Styled.Package.Page.ContentGrid>
                <YellowButton
                    style={{
                        height: '48px',
                        minHeight: '48px',
                        maxWidth: '200px',
                        textTransform: 'uppercase',
                        borderRadius: '8px',
                        alignSelf: 'center',
                        marginTop: '50px',
                        marginBottom: '20px',
                        fontSize: '20px',
                    }}
                    onClick={handleRedirect}
                    aria-label="Sair da ferramenta de videos"
                >
                    Sair
                </YellowButton>
            </Styled.Package.Content>
        </Styled.Global.Content>
    );
};

export default FVPacote;
